import keys from 'lodash/keys';
import CustomStore from 'devextreme/data/custom_store';
import { Queries } from './useQueries';
import { QueryListParams, SortDirection } from '#/graphql/types';
import { transformDateTimeValue } from '#/common/utils/datetimeTransform';
import DataStoreOptions from './DataStoreOptions';
import deepMap from '#/common/utils/deepMap';
import { NULL_STR } from '#/config/consts';

/**
 * Transform DevExtreme NOT filters from ['!', ['field', '=', 'value']] to ['field', '<>', 'value']
 * This handles the 'noneof' header filter behavior where DX wraps filters with '!' operator.
 */
const transformNotFilters = (expr: unknown): unknown => {
  if (!Array.isArray(expr)) return expr;

  // Check for NOT pattern: ['!', [field, '=', value]]
  if (
    expr.length === 2 &&
    expr[0] === '!' &&
    Array.isArray(expr[1]) &&
    expr[1].length === 3 &&
    typeof expr[1][0] === 'string' &&
    expr[1][1] === '='
  ) {
    const [field, , value] = expr[1];
    return [field, '<>', value];
  }

  // Recurse into nested arrays
  return expr.map((item) => transformNotFilters(item));
};

/** Normalise any filter by removing stacked outside brackets. */
const removeStackedFilters = (filter: unknown): unknown => {
  if (Array.isArray(filter) && filter.length === 1 && Array.isArray(filter[0])) {
    return removeStackedFilters(filter[0]);
  }
  return filter;
};

/** Interleave conditions with 'AND' to produce a flat filter expression. */
const joinWithAnd = (conditions: unknown[][]): unknown[] =>
  conditions
    .filter((cond) => cond.length > 0)
    .flatMap((cond, i) => (i === 0 ? [cond] : ['AND', cond]));

const createDataStore = <T>(queries: Queries<T>, options: DataStoreOptions<T>) => {
  const { columns, cacheEnabled, key = 'id', searchKey } = options;
  const { getItem, getItemList } = queries;

  let cache: T[] = [];

  return new CustomStore<T, string>({
    key,
    byKey: async (id) => {
      if (!id) {
        return null as T;
      }
      try {
        if (cacheEnabled && cache.length > 0) {
          const cachedItem = cache.find((item) => item[id as keyof T] === id);
          if (cachedItem) {
            return cachedItem as T;
          }
        }
        const response = await getItem(id);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status === 404) {
          return null as T;
        }

        if (result.status !== 200) {
          throw new Error(response.errors?.join('\n'));
        }

        return result.data as T;
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
    load: async (loadOptions) => {
      try {
        const sortOptions: any = Array.isArray(loadOptions.sort)
          ? loadOptions.sort[0]
          : loadOptions.sort;

        const rawFilter = deepMap<string | null>(loadOptions.filter || [], (value) =>
          value === NULL_STR ? null : value,
        );

        const searchCondition = loadOptions.searchValue
          ? [
              [
                searchKey || loadOptions.searchExpr,
                loadOptions.searchOperation,
                loadOptions.searchValue,
              ],
            ]
          : [];

        const filter = removeStackedFilters(
          joinWithAnd([
            [...rawFilter],
            ...searchCondition,
            [...(options.filter ? options.filter : [])],
          ]),
        );

        const sortField =
          typeof sortOptions?.selector === 'string'
            ? sortOptions.selector
            : sortOptions?.selector?.context?.column?.dataField;

        // Transform NOT filters ['!', ['field', '=', 'value']] to ['field', '<>', 'value']
        const normalizedFilter = transformNotFilters(filter);

        const params: QueryListParams = {
          filter: normalizedFilter ? JSON.stringify(normalizedFilter) : null,
          sortBy: sortField
            ? {
                field: sortField,
                direction: sortOptions.desc ? SortDirection.DESC : SortDirection.ASC,
              }
            : null,
          skip: loadOptions.skip ?? null,
          take: loadOptions.take ?? null,
          requireTotalCount: loadOptions.requireTotalCount ?? null,
          ...(options.accessTo ? { accessTo: options.accessTo } : {}),
        };

        const response = await getItemList(params);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status !== 200) {
          throw new Error(response.errors?.join('\n'));
        }

        // TODO: Remove this after fixing the datetime values on the backend
        const transformDataBasedOnColumns = (data: any[]): any[] => {
          if (!columns || !data) return data;

          // Get all columns that have dataType: 'date'
          const dateColumns = columns
            .filter((col) => col.dataType === 'date')
            .map((col) => col.dataField);

          return data.map((item) => {
            const transformed = { ...item };

            dateColumns.forEach((fieldName) => {
              if (fieldName in transformed) {
                transformed[fieldName] = transformDateTimeValue(transformed[fieldName]);
              }
            });

            columns
              .map((col) => col.dataField)
              .forEach((fieldName) => {
                if (fieldName in transformed && transformed[fieldName] === null) {
                  transformed[fieldName] = NULL_STR;
                }
              });

            return transformed;
          });
        };

        const transformedData = transformDataBasedOnColumns(result.data ?? []);

        if (cacheEnabled) {
          cache = transformedData;
        }

        return {
          data: transformedData,
          totalCount: result.totalCount,
        };
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
  });
};

export default createDataStore;
